import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronsUpDown, Plus } from 'lucide-react';
import { StatusBadge } from './StatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ClientPicker({ value, onChange, error }) {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, {
        withCredentials: true,
      });
      setClients(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    if (value && clients.length > 0) {
      const client = clients.find((c) => c.id === value);
      setSelectedClient(client);
    }
  }, [value, clients]);

  const filtered = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (client) => {
    setSelectedClient(client);
    onChange(client.id, client.name);
    setOpen(false);
    setSearch('');
  };

  const handleCreateNew = async () => {
    if (!search.trim()) return;
    try {
      const { data } = await axios.post(
        `${API}/clients`,
        {
          name: search.trim(),
          status: 'lead',
          priority: 'medium',
        },
        { withCredentials: true }
      );
      setClients([...clients, data]);
      handleSelect(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error creating client:', error);
    }
  };

  return (
    <div className="w-full">
      {selectedClient ? (
        <div
          className="bg-surface-raised border border-border rounded-lg px-4 py-2 flex items-center justify-between"
          data-testid="client-picker-selected"
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-background font-medium text-sm">
              {selectedClient.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-foreground font-medium">{selectedClient.name}</div>
              <div className="text-xs text-text-secondary">{selectedClient.email || 'No email'}</div>
            </div>
            <StatusBadge status={selectedClient.status} />
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedClient(null);
              onChange(null);
            }}
            className="text-text-secondary hover:text-foreground text-sm"
            data-testid="client-picker-change"
          >
            Change
          </button>
        </div>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="w-full bg-surface-raised border border-border rounded-lg px-4 py-2 text-left flex items-center justify-between text-foreground hover:border-primary transition-colors"
            data-testid="client-picker-button"
          >
            <span className={value ? 'text-foreground' : 'text-text-muted'}>
              {value ? 'Select client' : 'Select a client...'}
            </span>
            <ChevronsUpDown className="w-4 h-4 text-text-muted" />
          </button>
          {open && (
            <div className="absolute z-50 w-full mt-2 bg-surface border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <div className="p-2 border-b border-border">
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full bg-surface-raised border border-border rounded px-3 py-2 text-foreground placeholder-text-muted focus:border-primary focus:outline-none"
                  data-testid="client-picker-search"
                />
              </div>
              <div className="p-1">
                {filtered.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    onClick={() => handleSelect(client)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-raised rounded text-left transition-colors"
                    data-testid={`client-picker-option-${client.id}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-background text-xs font-medium">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="text-foreground text-sm">{client.name}</div>
                      <div className="text-xs text-text-secondary">{client.email || 'No email'}</div>
                    </div>
                    <StatusBadge status={client.status} />
                  </button>
                ))}
                {search && (
                  <button
                    type="button"
                    onClick={handleCreateNew}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-surface-raised rounded text-left text-primary transition-colors"
                    data-testid="client-picker-create-new"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">Add &quot;{search}&quot; as new client</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {error && <div className="text-alert text-sm mt-1">{error}</div>}
    </div>
  );
}
